<script setup lang="ts">
import { type ID, type User } from '#shared'
import { computed, ref, useTemplateRef } from 'vue'
import type { Reaction } from '#entities/message'
import { useAddReactionMutation } from '../state/use-add-reaction-mutation'
import { useDeleteReactionMutation } from '../state/use-delete-reaction-mutation'
import ReactionsItemPopup from './reactions-item-popup.vue'

const {
  chatId,
  messageId,
  char,
  senders,
  currentUserReaction,
  disabled = false,
} = defineProps<{
  chatId: ID
  messageId: ID
  char: string
  senders: User[]
  currentUserReaction?: Reaction
  disabled?: boolean
}>()

const checkboxId = computed(() => `${messageId}-${char}`)

const addReactionMutation = useAddReactionMutation(chatId, messageId)
const deleteReactionMutation = useDeleteReactionMutation(chatId, messageId)

const onChange = (event: Event) => {
  if (disabled) return

  const target = event.target as HTMLInputElement

  if (target.checked) {
    addReactionMutation.mutate({ char })
  } else {
    if (currentUserReaction && !currentUserReaction.meta?.isSending) {
      deleteReactionMutation.mutate(currentUserReaction?.id)
    }
  }
}

const rootEl = useTemplateRef('rootEl')
const isHovered = ref(false)
const onHover = () => (isHovered.value = true)
const onLeave = () => (isHovered.value = false)
</script>

<template>
  <div :class="$style.reaction" ref="rootEl" @mouseenter="onHover" @mouseleave="onLeave">
    <input
      :class="$style.checkbox"
      type="checkbox"
      :id="checkboxId"
      :checked="!!currentUserReaction"
      :disabled="disabled"
      @change="onChange"
    />

    <label
      :class="[$style.view, { [$style.viewSelected]: !!currentUserReaction, [$style.viewDisabled]: disabled }]"
      :for="checkboxId"
    >
      <div :class="$style.char">{{ char }}</div>
      <div :class="$style.count">{{ senders.length }}</div>
    </label>

    <ReactionsItemPopup :char="char" :senders="senders" :reactionItemEl="rootEl" :isShown="isHovered" />
  </div>
</template>

<style lang="scss" module>
.reaction {
  display: flex;
}

.checkbox {
  display: none;
}

.view {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  border: 1px solid transparent;
  padding: 2px 4px;
  border-radius: 4px;
  gap: 4px;
  background-color: #2b2d4b;
}

.viewSelected {
  border-color: #0d4fc1;
}

.viewDisabled {
  cursor: default !important;
}

.char {
  font-family: 'Noto Color Emoji', serif;
  line-height: 18px;
}

.count {
  font-size: 14px;
}
</style>
